import React, { useState, useEffect } from "react";
import { BrowserRouter as Router, Route, Routes, Navigate } from "react-router-dom";
import { createPortal } from "react-dom"; // For rendering popup in body
import "bootstrap/dist/css/bootstrap.min.css";

import Navbar from "./components/Navbar";
import Footer from "./components/Footer";
import CookieConsent from "./components/CookieConsent";
import Home from "./pages/Home";
import Products from "./pages/Products";
import Industries from "./pages/Industries";
import Download from "./pages/Download";
import Contact from "./pages/ContactUs";
import AboutUs from "./pages/AboutUs";
import Careers from "./pages/Careers";
import ProductDetails from "./pages/ProductDetails";
import SalesPartners from "./pages/SalesPartners";
import Faqs from "./pages/Faqs";
import Privacy from "./pages/Privacy";
import Terms from "./pages/Terms";
import WritingDetails from "./pages/WritingDetails";
import News from "./pages/News";

import "./pages/Main.css";
import gifIntro from "./images/intro-gif-2.webp";
import "./pages/Preloader.css";

import ScrollToTop from "./components/ScrollTop";
import Chatbot from "./components/Chatbot";
import { Mail, Phone, User } from "lucide-react"; // For form icons

function App() {
  const [isLoading, setIsLoading] = useState(true);
  const [showChat, setShowChat] = useState(false);

  // Popup state
  const [showFormPopup, setShowFormPopup] = useState(false);
  const [formSubmitted, setFormSubmitted] = useState(false);
  const [showThankYouPopup, setShowThankYouPopup] = useState(false);
  const [formData, setFormData] = useState({
    fullName: "",
    email: "",
    phone: "",
    message: "",
  });
  const [responseMessage, setResponseMessage] = useState("");
  const [loading, setLoading] = useState(false);

  // Handle input changes
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prevData) => ({
      ...prevData,
      [name]: value,
    }));
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setResponseMessage("");

    // Basic validation
    if (!formData.fullName || !formData.email || !formData.phone || !formData.message) {
      setResponseMessage("Please fill in all fields.");
      setLoading(false);
      return;
    }

    const formBody = new URLSearchParams(formData).toString();

    try {
      const response = await fetch("https://peracause.com/backend-files/premier-pumps-usa/contact-form-premier-pumps.php", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: formBody,
      });

      const result = await response.json();

      if (result.status === "success") {
        setResponseMessage("Message sent successfully! ✅");
        setFormData({
          fullName: "",
          email: "",
          phone: "",
          message: "",
        });
        setShowFormPopup(false); // Close form
        setShowThankYouPopup(true); // Open thank-you popup
      } else {
        setResponseMessage("Error: " + result.message);
      }
    } catch (error) {
      setResponseMessage("Error sending message. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  // Handle reach out
  const handleReachOut = () => {
    setShowFormPopup(true);
    setFormSubmitted(false); // Reset form submission when opening popup
  };

  useEffect(() => {
    const timer = setTimeout(() => setIsLoading(false), 3000);
    return () => clearTimeout(timer);
  }, []);

  return (
    <Router>
      {/* {isLoading ? (
        <div className="preloader">
          <img src={gifIntro} alt="Loading..." className="preloader-gif" />
        </div>
      ) : ( */}
        <>
          <Navbar onReachOut={handleReachOut} />
          <ScrollToTop />
          <div>
            <Routes>
              <Route path="/" element={<Home />} />
              <Route path="/products" element={<Products />} />
              <Route path="/services" element={<Navigate to="/" state={{ scrollTo: "services" }} />} />
              <Route path="/quality" element={<Navigate to="/" state={{ scrollTo: "quality" }} />} />
              <Route path="/industries" element={<Industries />} />
              <Route path="/download" element={<Download />} />
              <Route path="/contact-us" element={<Contact />} />
              <Route path="/about-us" element={<AboutUs />} />
              <Route path="/careers" element={<Careers />} />
              <Route path="/sales-partners" element={<SalesPartners />} />
              <Route path="/product-details/:category" element={<ProductDetails />} />
              <Route path="/faqs" element={<Faqs />} />
              <Route path="/terms" element={<Terms />} />
              <Route path="/privacy" element={<Privacy />} />
              <Route path="/writing-details/:id" element={<WritingDetails />} />
              <Route path="/news" element={<News />} />
            </Routes>
          </div>
          <Footer />
          <CookieConsent />

          {/* Chatbot Toggle Button */}
          <div className="chatbot-container">
            {showChat && <Chatbot />}
            <button className="chatbot-btn" onClick={() => setShowChat(!showChat)}>
              💬 Chat
            </button>
          </div>

          {/* Form Popup (Rendered in body via Portal) */}
          {showFormPopup &&
            createPortal(
              <div className="popup-overlay">
                <div className="popup-box">
                  <div className="popup-header d-flex justify-content-center align-items-center">
                    <h5>{formSubmitted ? "Thank You!" : "Reach out to us !"}</h5>
                    <button
                      className="btn-close fs-4"
                      onClick={() => setShowFormPopup(false)}
                    >
                      ×
                    </button>
                  </div>
                  <div className="form-container">
                    {!formSubmitted ? (
                      <form onSubmit={handleSubmit}>
                        <div className="input-group flex-column">
                          {/* Full Name */}
                          <label className="fw-medium">
                             Name <span className="text-danger">*</span>
                          </label>
                          <div className="input-wrapper rounded-20 my-1 p-2">
                            <div className="d-flex align-items-center">
                              <User size={20} className="input-icon text-blue me-2" />
                              <input
                                id="fullName"
                                name="fullName"
                                type="text"
                                placeholder="Enter your name"
                                className="form-control border-start rounded-0 ps-2"
                                required
                                value={formData.fullName}
                                onChange={handleChange}
                              />
                            </div>
                          </div>
                          {/* Email */}
                          <label className="fw-medium">
                            Email <span className="text-danger">*</span>
                          </label>
                          <div className="input-wrapper rounded-20 my-1 p-2">
                            <div className="d-flex align-items-center">
                              <Mail size={20} className="input-icon text-blue me-2" />
                              <input
                                id="email"
                                name="email"
                                type="email"
                                placeholder="Enter your email"
                                className="form-control border-start rounded-0 ps-2"
                                required
                                value={formData.email}
                                onChange={handleChange}
                              />
                            </div>
                          </div>
                          {/* Phone */}
                          <label className="fw-medium">Phone</label>
                          <div className="input-wrapper rounded-20 my-1 p-2">
                            <div className="d-flex align-items-center">
                              <Phone size={20} className="input-icon text-blue me-2" />
                              <input
                                id="phone"
                                name="phone"
                                type="tel"
                                placeholder="Enter your phone number"
                                className="form-control border-start rounded-0 ps-2"
                                value={formData.phone}
                                onChange={handleChange}
                                onKeyDown={(e) => {
                                  const allowedKeys = [
                                    "Backspace",
                                    "ArrowLeft",
                                    "ArrowRight",
                                    "Delete",
                                    "Tab",
                                    "Home",
                                    "End",
                                    "+",
                                    "-",
                                    "(",
                                    ")",
                                    " ",
                                  ];
                                  const isNumber = /^[0-9]$/.test(e.key);
                                  const isAllowed = allowedKeys.includes(e.key);
                                  if (!isNumber && !isAllowed) e.preventDefault();
                                }}
                              />
                            </div>
                          </div>
                          {/* Message */}
                          <label className="fw-medium mt-3">Message</label>
                          <div className="input-wrapper rounded-20 my-1 p-2">
                            <textarea
                              id="message"
                              name="message"
                              placeholder="Your Message"
                              rows="4"
                              className="form-control rounded-0 ps-2"
                              required
                              value={formData.message}
                              onChange={handleChange}
                            />
                          </div>
                          {/* Submit */}
                          <div className="d-flex justify-content-center">
                            <button type="submit" className="btn btn-primary rounded-20 my-1 px-4 py-3">
                              {loading ? "Sending..." : "Submit"}
                            </button>
                          </div>
                        </div>
                        {responseMessage && <p className="text-center mt-3">{responseMessage}</p>}
                      </form>
                    ) : (
                      <div className="text-center p-4">
                        <p>Thank you for reaching out! We'll get back to you soon.</p>
                        <button
                          className="btn btn-primary mt-3 px-4"
                          onClick={() => {
                            setShowFormPopup(false);
                          }}
                        >
                          Close
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              </div>,
              document.body
            )}

          {/* Thank You Popup (Rendered in body via Portal) */}
          {showThankYouPopup &&
            createPortal(
              <div className="thankyou-popup-overlay d-flex align-items-center justify-content-center" id="thankyou-popup-overlay">
                <div className="thankyou-popup-box position-relative" id="thankyou-popup-box">
                  <div className="thankyou-popup-body text-center d-flex align-items-center justify-content-center flex-column">
                    <h5 className="mb-3 fw-bold text-blue fs-3">Thank you for reaching out to us.</h5>
                    <p>
                      You can also contact PREMIER at <a href="mailto:support@premierpumps.com">support@premierpumps.com</a>
                    </p>
                    <button
                      className="btn btn-primary mt-3 px-4"
                      onClick={() => setShowThankYouPopup(false)}
                    >
                      Close
                    </button>
                  </div>
                </div>
              </div>,
              document.body
            )}
        </>
      {/* )} */}
    </Router>
  );
}

export default App;